import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewFilters from '@app/src/components/Drawer/Views/DrawerViewFilters'
import { OptionIcon } from '@app/src/components/Form/Select'
import { FinalRiskRatingIcon } from '@app/src/components/Table/Cells/FinalRiskRatingCell'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { useGroupBySelector } from '@app/src/context/GroupBySelectorContext'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useAiScreeningEnabled from '@app/src/hooks/useAiScreeningEnabled'
import {
  responseItemOrganizationFacetSettings,
  responseItemPeriodFacetSettings,
  responseItemProviderIdFacetSettings,
  responseItemQuestionFacetSettings,
  responseItemQuestionnaireFacetSettings,
} from '@app/src/pages/ResourceCollection/Filters/Common/ResponseItemFacetSettings'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import { PeriodFilterOptions } from '@app/src/pages/ResourceCollection/Filters/PeriodFilterDisplay'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators, RESPONSE_ITEM_LATEST_SUBMITTED_FILTER } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import { QuestionType } from '@app/src/types/reporting'
import { RequestTargetType } from '@app/src/types/resourceExplorer'
import { comparePeriods } from '@app/src/utils/getOrderedPeriods'
import { AmplitudeTrackingEvents, ONE_MINUTE_MS, Solutions } from '@app/src/wf-constants'
import { Box, Button, Chip, Grid, Tooltip } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'
import AllFiltersButton from './AllFiltersButton'
import AssessmentTemplateSelectorWithLevelFilter from './AssessmentMiniFilter/AssessmentTemplateSelectorWithLevelFilter'
import useLocalisedCountryFilter from './Common/useLocalisedCountryFilter'
import CustomCategoriesFilters from './CustomCategoriesFilters'
import { DatahubPeriodFilter } from './DatahubPeriodFilter'
import FilterFacetSelect from './FilterFacetSelect'
import FilterSection from './FilterSection'
import FilterSelect from './FilterSelect'
import { GroupByFilter } from './GroupByFilter'
import OptionWithLabel from './OptionWithLabel'
import RangeFilter from './RangeFilter'
import SpendFilter from './SpendFilter'
import { AnswersGroupingType } from './StatisticsConstants'
import StatusOption from './StatusOption'

interface DatahubDataInsightsFiltersProps {
  allowedFilters: string[]
  implicitFilters?: FilterGroup[]
  anyQuestionFilterSet?: boolean
  isDetailsTabActive?: boolean
}

const DatahubDataInsightsFilters: React.FC<DatahubDataInsightsFiltersProps> = ({
  allowedFilters,
  implicitFilters,
  anyQuestionFilterSet,
  isDetailsTabActive,
}) => {
  const { formatMessage } = useIntl()
  const legislationCategoryName = 'Legislation'
  const { openDrawer } = useDrawer()
  const { hasPermission, renderWithPermission } = usePermissions()
  const verificationEnabled = hasPermission(Permissions.WRITE_VERIFICATION)
  const { solution } = useAuthentication().scope
  const { productMapping } = useFlags()
  const isAiScreeningEnabled = useAiScreeningEnabled()
  const { trackEvent } = useAmplitude()
  const canAccessAssessmentTemplate = hasPermission(Permissions.ASSESSMENT_TEMPLATE_ACCESS)
  const userFilters = useGetApiQueryFilters(allowedFilters)
  const { setGroupBy } = useGroupBySelector()

  const { renderOption, renderTags, customFilterOption, customSort } = useLocalisedCountryFilter()

  const {
    facets: [availablePeriodNames = []],
  } = useFetchFacets({
    key: [FetchKey.FilterFacet, 'periodName'],
    endpoint: endpoints.responseItemsWithFacets,
    facetsParam: [{ name: 'response.request.periodName', isEnum: true }],
    filter: [...userFilters, RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
    sort: {
      order: SortOrder.DESCENDING,
      target: 'value',
    },
    options: { staleTime: ONE_MINUTE_MS },
  })

  const first3Periods = availablePeriodNames.sort((a, b) => -comparePeriods(a.label, b.label)).slice(0, 3)

  return (
    <Grid container spacing={1}>
      <Filters allowedFilters={allowedFilters}>
        {({ clearQueryFilter, inDrawerFiltersCount, isFiltered }) => {
          return (
            <>
              <Grid item xs={2}>
                <Filter name="requestItem.template.options.id" operator={Operators.In}>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      multiple
                      facetsParam={{
                        key: [FetchKey.ResponseItemsFacets, filterName],
                        endpoint: endpoints.responseItemsWithFacets,
                        facetsParam: [{ name: 'response.request.template.sections.questions.options.name' }],
                        sort: { target: 'label', order: SortOrder.ASCENDING },
                        filter: [
                          {
                            name: 'response.request.template.sections.questions.options.category.name',
                            filters: [{ value: legislationCategoryName, operator: Operators.EqualTo }],
                          },
                          RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
                        ],
                      }}
                      size="small"
                      filterName={filterName}
                      onChange={onChange}
                      fieldLabel={formatMessage({ id: 'schemas.question.legislation' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item xs={2}>
                <Filter name="response.request.questionnaireTemplateId" operator={Operators.In}>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      multiple
                      facetsParam={responseItemQuestionnaireFacetSettings([FetchKey.ResponseItemsFacets, filterName])}
                      size="small"
                      filterName={filterName}
                      onChange={onChange}
                      fieldLabel={formatMessage({ id: 'resourceTypes.questionnaires' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item xs={2}>
                <Filter name="requestItem.questionId" operator={Operators.In}>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      size="small"
                      multiple
                      facetsParam={responseItemQuestionFacetSettings([FetchKey.ResponseItemsFacets, filterName])}
                      filterName={filterName}
                      onChange={onChange}
                      fieldLabel={formatMessage({ id: 'dataHub.selectQuestions' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
              <GroupByFilter isDetailsTabActive={isDetailsTabActive} />
              <DatahubPeriodFilter
                preSelectedValues={first3Periods.map(f => f.value)}
                allowedFilters={allowedFilters}
                anyQuestionFilterSet={anyQuestionFilterSet}
              />
              <Grid item lg={2} sm={3} xs={12}>
                <Box display="inline-block">
                  <AllFiltersButton
                    inDrawerFiltersCount={inDrawerFiltersCount}
                    openDrawer={() =>
                      openDrawer(
                        <DrawerViewFilters allowedFilters={allowedFilters}>
                          <Box px={2} mt={2}>
                            <Filter name="response.request.periodName" operator={Operators.In}>
                              {({ value, onChange, filterName }) => (
                                <FilterFacetSelect
                                  implicitFilters={implicitFilters}
                                  size="small"
                                  multiple
                                  facetsParam={responseItemPeriodFacetSettings([
                                    FetchKey.ResponseItemsFacets,
                                    filterName,
                                  ])}
                                  filterName={filterName}
                                  onChange={(value: string[]) => onChange(value)}
                                  fieldLabel={formatMessage({ id: 'schemas.request.period' })}
                                  value={[value].flat()}
                                  {...PeriodFilterOptions}
                                />
                              )}
                            </Filter>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.dataInsights' })}>
                              <Filter name="requestItem.template.options.id" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [
                                        { name: 'response.request.template.sections.questions.options.name' },
                                      ],
                                      sort: { target: 'label', order: SortOrder.ASCENDING },
                                      filter: [
                                        {
                                          name: 'response.request.template.sections.questions.options.category.name',
                                          filters: [{ value: legislationCategoryName, operator: Operators.EqualTo }],
                                        },
                                        RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
                                      ],
                                    }}
                                    size="small"
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.question.legislation' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter name="response.request.questionnaireTemplateId" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    multiple
                                    facetsParam={responseItemQuestionnaireFacetSettings([
                                      FetchKey.ResponseItemsFacets,
                                      filterName,
                                    ])}
                                    size="small"
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'resourceTypes.questionnaires' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter name="requestItem.questionId" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    size="small"
                                    multiple
                                    facetsParam={responseItemQuestionFacetSettings([
                                      FetchKey.ResponseItemsFacets,
                                      filterName,
                                    ])}
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'dataHub.selectQuestions' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                            </FilterSection>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.answersDetails' })}>
                              {productMapping && (
                                <>
                                  <Filter name="response.request.subscriptions.targetType" operator={Operators.EqualTo}>
                                    {({ value, onChange }) => (
                                      <FilterSelect
                                        size="small"
                                        multiple={false}
                                        options={[
                                          {
                                            value: RequestTargetType.Product,
                                            label: formatMessage({ id: 'schemas.mappingNode.product' }),
                                          },
                                          {
                                            value: RequestTargetType.Provider,
                                            label: formatMessage({ id: 'resourceCollections.general.company' }),
                                          },
                                        ]}
                                        onChange={value => onChange(value)}
                                        fieldLabel={formatMessage({ id: 'reporting.requestedFor' })}
                                        value={value as string}
                                      />
                                    )}
                                  </Filter>
                                  <Filter name="response.request.subscriptions.targetProductId" operator={Operators.In}>
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [{ name: 'response.request.subscriptions.targetProduct.name' }],
                                        }}
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.mappingNode.product' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}
                              <Filter name="response.request.subscriptions.targetProviderId" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    multiple
                                    facetsParam={responseItemProviderIdFacetSettings([
                                      FetchKey.ResponseItemsFacets,
                                      filterName,
                                    ])}
                                    size="small"
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.companyId' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter name="response.request.subscriptions.targetProviderId" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    multiple
                                    facetsParam={responseItemOrganizationFacetSettings([
                                      FetchKey.ResponseItemsFacets,
                                      filterName,
                                    ])}
                                    size="small"
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.companyName' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter name="response.request.subscriptions.target.country.id" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [
                                        { name: 'response.request.subscriptions.target.country.iso3166Alpha2' },
                                      ],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    renderOption={renderOption}
                                    renderTags={renderTags}
                                    size="small"
                                    filterName={filterName}
                                    filterOptions={customFilterOption}
                                    customSort={customSort}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.country.countryCode' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              {verificationEnabled && (
                                <>
                                  <Filter
                                    name="response.verifications.externalVerificationStatus"
                                    operator={Operators.In}
                                  >
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [{ name: filterName }],
                                          filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                        }}
                                        size="small"
                                        renderOption={(props, option, { selected }) => (
                                          <StatusOption
                                            key={option.label}
                                            props={props}
                                            option={option}
                                            selected={selected}
                                            verification
                                          />
                                        )}
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({
                                          id: 'filters.responseItem.externalVerificationStatus',
                                        })}
                                        value={[value].flat()}
                                        skipNotSet={false}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Tooltip
                                                  key={option?.value?.toString() || index}
                                                  title={formatMessage({
                                                    id: `reporting.reviewStatuses.${option.label}`,
                                                  })}
                                                  sx={{ mt: 2 }}
                                                  arrow
                                                >
                                                  <Chip
                                                    label={formatMessage({
                                                      id: `reporting.reviewStatuses.${option.label}`,
                                                    })}
                                                    size="small"
                                                    {...getTagProps({ index })}
                                                  />
                                                </Tooltip>
                                              ),
                                          )
                                        }
                                      />
                                    )}
                                  </Filter>

                                  <Filter
                                    name="response.verifications.internalVerificationStatus"
                                    operator={Operators.In}
                                  >
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [{ name: filterName }],
                                          filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                        }}
                                        size="small"
                                        renderOption={(props, option, { selected }) => (
                                          <StatusOption
                                            key={option.label}
                                            props={props}
                                            option={option}
                                            selected={selected}
                                            verification
                                          />
                                        )}
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({
                                          id: 'filters.responseItem.internalVerificationStatus',
                                        })}
                                        value={[value].flat()}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Tooltip
                                                  key={option?.value?.toString() || index}
                                                  title={formatMessage({
                                                    id: `reporting.internalVerificationStatuses.${option.label}`,
                                                  })}
                                                  sx={{ mt: 2 }}
                                                  arrow
                                                >
                                                  <Chip
                                                    label={formatMessage({
                                                      id: `reporting.internalVerificationStatuses.${option.label}`,
                                                    })}
                                                    size="small"
                                                    {...getTagProps({ index })}
                                                  />
                                                </Tooltip>
                                              ),
                                          )
                                        }
                                        skipNotSet={false}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}
                              <RangeFilter
                                uniqueIdFrom="responseDateFrom"
                                uniqueIdTo="responseDateTo"
                                name="response.submittedAt"
                                fieldLabelFrom={formatMessage({ id: 'schemas.response.responseDateFrom' })}
                                fieldLabelTo={formatMessage({ id: 'schemas.response.responseDateTo' })}
                              />
                              <Filter name="requestItem.questionTypeId" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'requestItem.questionType.name' }],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <OptionWithLabel
                                        key={option.value?.toString()}
                                        props={props}
                                        label={formatMessage({ id: `reporting.questionType.${option.label}` })}
                                        multiple
                                        selected={selected}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              label={formatMessage({ id: `reporting.questionType.${option.label}` })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={selectedNames =>
                                      onChange(
                                        (selectedNames as string[]).map(name =>
                                          String(QuestionType[name as keyof typeof QuestionType]),
                                        ),
                                      )
                                    }
                                    fieldLabel={formatMessage({ id: 'schemas.filter.questionType' })}
                                    value={[value]
                                      .flat()
                                      .map(id => QuestionType[Number(id)])
                                      .filter(Boolean)}
                                  />
                                )}
                              </Filter>
                            </FilterSection>
                            {isAiScreeningEnabled && (
                              <FilterSection label={formatMessage({ id: 'schemas.request.source' })}>
                                <Filter operator={Operators.EqualTo} name="isScraped">
                                  {({ value, onChange }) => (
                                    <FilterSelect
                                      size="small"
                                      multiple={false}
                                      options={[
                                        { value: true, label: formatMessage({ id: 'general.aiScreening' }) },
                                        { value: false, label: formatMessage({ id: 'general.selfReported' }) },
                                      ]}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.request.source' })}
                                      value={Array.isArray(value) ? value?.[0] : value}
                                    />
                                  )}
                                </Filter>
                              </FilterSection>
                            )}
                            <SpendFilter
                              spendFilterNamePrefix="response.request.subscriptions.target"
                              defaultCollapsed
                            />
                            {canAccessAssessmentTemplate && (
                              <AssessmentTemplateSelectorWithLevelFilter
                                fetchKey={FetchKey.ResponseItemsFacets}
                                facetEndpoint={endpoints.responseItemsWithFacets}
                                allowedFilters={allowedFilters}
                                assessmentFilterNamePrefix="response.request.subscriptions.target.assessments"
                              />
                            )}
                            <FilterSection
                              defaultCollapsed
                              label={formatMessage({ id: 'schemas.filter.additionalInformation' })}
                            >
                              <Filter
                                operator={Operators.In}
                                name="response.request.subscriptions.target.providerOwners.userId"
                              >
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [
                                        { name: 'response.request.subscriptions.target.providerOwners.user.name' },
                                      ],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.ownerUserIds' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter
                                operator={Operators.In}
                                name="response.request.subscriptions.target.finalRiskRating"
                              >
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'response.request.subscriptions.target.finalRiskRating' }],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <li {...props}>
                                        <OptionIcon selected={selected} multiple />
                                        <FinalRiskRatingIcon value={option?.label} />
                                        {formatMessage({
                                          id: option?.label
                                            ? `schemas.provider.finalRiskRatingValues.${option.label}`
                                            : 'general.notAvailable',
                                        })}
                                      </li>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              icon={<FinalRiskRatingIcon value={option.label} />}
                                              label={formatMessage({
                                                id: `schemas.provider.finalRiskRatingValues.${option.label}`,
                                              })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={value => {
                                      if (value.length) {
                                        trackEvent({
                                          name: AmplitudeTrackingEvents.Analyze.Filter.RiskAssessment,
                                          eventProps: {
                                            filter_value: value,
                                          },
                                        })
                                      }

                                      onChange(value)
                                    }}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.finalRiskRating' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter operator={Operators.In} name="response.request.subscriptions.target.priority">
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseItemsFacets, filterName],
                                      endpoint: endpoints.responseItemsWithFacets,
                                      facetsParam: [{ name: 'response.request.subscriptions.target.priority' }],
                                      filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <OptionWithLabel
                                        key={option.label}
                                        props={props}
                                        label={formatMessage({
                                          id: option?.label
                                            ? `schemas.provider.priorityValues.${option.label}`
                                            : 'general.notAvailable',
                                        })}
                                        multiple
                                        selected={selected}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              label={formatMessage({
                                                id: `schemas.provider.priorityValues.${option.label}`,
                                              })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.priority' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              {solution !== Solutions.Finance && (
                                <>
                                  <Filter
                                    operator={Operators.In}
                                    name="response.request.subscriptions.target.activityStatus"
                                  >
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [
                                            { name: 'response.request.subscriptions.target.activityStatus' },
                                          ],
                                          filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                        }}
                                        renderOption={(props, option, { selected }) => (
                                          <OptionWithLabel
                                            key={option.label}
                                            props={props}
                                            label={formatMessage({
                                              id: option?.label
                                                ? `schemas.provider.activityStatusValues.${option.label}`
                                                : 'general.notAvailable',
                                            })}
                                            multiple
                                            selected={selected}
                                          />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Chip
                                                  {...getTagProps({ index })}
                                                  key={option?.value?.toString() || index}
                                                  label={formatMessage({
                                                    id: `schemas.provider.activityStatusValues.${option.label}`,
                                                  })}
                                                  size="small"
                                                />
                                              ),
                                          )
                                        }
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.provider.activityStatus' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>
                                  <Filter
                                    operator={Operators.In}
                                    name="response.request.subscriptions.target.providerApprovalStatus"
                                  >
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseItemsFacets, filterName],
                                          endpoint: endpoints.responseItemsWithFacets,
                                          facetsParam: [
                                            { name: 'response.request.subscriptions.target.providerApprovalStatus' },
                                          ],
                                          filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                        }}
                                        renderOption={(props, option, { selected }) => (
                                          <OptionWithLabel
                                            key={option.label}
                                            props={props}
                                            label={formatMessage({
                                              id: option?.label
                                                ? `schemas.provider.providerApprovalStatusValues.${option.label}`
                                                : 'general.notAvailable',
                                            })}
                                            multiple
                                            selected={selected}
                                          />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Chip
                                                  {...getTagProps({ index })}
                                                  key={option?.value?.toString() || index}
                                                  label={formatMessage({
                                                    id: `schemas.provider.providerApprovalStatusValues.${option.label}`,
                                                  })}
                                                  size="small"
                                                />
                                              ),
                                          )
                                        }
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.provider.providerApprovalStatus' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}
                              {renderWithPermission({
                                [Permissions.SOURCING_USER]: (
                                  <>
                                    <Filter
                                      operator={Operators.In}
                                      name="response.request.subscriptions.target.supplierUsage"
                                    >
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseItemsFacets, filterName],
                                            endpoint: endpoints.responseItemsWithFacets,
                                            facetsParam: [
                                              { name: 'response.request.subscriptions.target.supplierUsage' },
                                            ],
                                            filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                          }}
                                          renderOption={(props, option, { selected }) => (
                                            <OptionWithLabel
                                              key={option.label}
                                              props={props}
                                              label={formatMessage({
                                                id: option?.label
                                                  ? `schemas.provider.supplierUsageValues.${option.label}`
                                                  : 'general.notAvailable',
                                              })}
                                              multiple
                                              selected={selected}
                                            />
                                          )}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Chip
                                                    {...getTagProps({ index })}
                                                    key={option?.value?.toString() || index}
                                                    label={formatMessage({
                                                      id: `schemas.provider.supplierUsageValues.${option.label}`,
                                                    })}
                                                    size="small"
                                                  />
                                                ),
                                            )
                                          }
                                          filterName={filterName}
                                          onChange={onChange}
                                          fieldLabel={formatMessage({ id: 'schemas.provider.supplierUsage' })}
                                          value={[value].flat()}
                                        />
                                      )}
                                    </Filter>
                                    <Filter operator={Operators.In} name="response.request.subscriptions.target.tier">
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseItemsFacets, filterName],
                                            endpoint: endpoints.responseItemsWithFacets,
                                            facetsParam: [
                                              { name: 'response.request.subscriptions.target.tier', isEnum: true },
                                            ],
                                            filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                          }}
                                          renderOption={(props, option, { selected }) => (
                                            <OptionWithLabel
                                              key={option.label}
                                              props={props}
                                              label={formatMessage(
                                                {
                                                  id: `schemas.provider.tierValues`,
                                                },
                                                { tier: option?.label ?? '' },
                                              )}
                                              multiple
                                              selected={selected}
                                            />
                                          )}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Chip
                                                    {...getTagProps({ index })}
                                                    key={option?.value?.toString() || index}
                                                    label={formatMessage(
                                                      {
                                                        id: `schemas.provider.tierValues`,
                                                      },
                                                      { tier: option.label },
                                                    )}
                                                    size="small"
                                                  />
                                                ),
                                            )
                                          }
                                          filterName={filterName}
                                          onChange={onChange}
                                          fieldLabel={formatMessage({ id: 'schemas.provider.tier' })}
                                          value={[value].flat()}
                                        />
                                      )}
                                    </Filter>
                                  </>
                                ),
                              })}
                            </FilterSection>
                            <CustomCategoriesFilters baseFilterName="response.request.subscriptions.target" />

                            {productMapping && (
                              <FilterSection defaultCollapsed label={formatMessage({ id: 'navbar.productMapping' })}>
                                <Filter
                                  operator={Operators.In}
                                  name="response.request.subscriptions.target.mappingNodes.actorType.id"
                                >
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseItemsFacets, filterName],
                                        endpoint: endpoints.responseItemsWithFacets,
                                        facetsParam: [
                                          {
                                            name: 'response.request.subscriptions.target.mappingNodes.actorType.name',
                                          },
                                        ],
                                      }}
                                      renderOption={(props, option, { selected }) => (
                                        <OptionWithLabel
                                          key={option.label}
                                          props={props}
                                          label={formatMessage({
                                            id: option?.label
                                              ? `schemas.mappingNode.actorTypeValues.${option.label}`
                                              : 'general.notAvailable',
                                          })}
                                          multiple
                                          selected={selected}
                                        />
                                      )}
                                      renderTags={(value, getTagProps) =>
                                        value?.map(
                                          (option, index) =>
                                            option && (
                                              <Chip
                                                {...getTagProps({ index })}
                                                key={option?.value?.toString() || index}
                                                label={formatMessage({
                                                  id: `schemas.mappingNode.actorTypeValues.${option.label}`,
                                                })}
                                                size="small"
                                              />
                                            ),
                                        )
                                      }
                                      filterName={filterName}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.mappingNode.actorType' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                                <Filter
                                  operator={Operators.In}
                                  name="response.request.subscriptions.target.mappingNodes.tier"
                                >
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseItemsFacets, filterName],
                                        endpoint: endpoints.responseItemsWithFacets,
                                        facetsParam: [
                                          {
                                            name: 'response.request.subscriptions.target.mappingNodes.tier',
                                            isEnum: true,
                                          },
                                        ],
                                        filter: [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER],
                                      }}
                                      filterName={filterName}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.mappingNode.tier' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </FilterSection>
                            )}
                          </Box>
                        </DrawerViewFilters>,
                      )
                    }
                  />
                </Box>

                {isFiltered && (
                  <Button
                    variant="text"
                    onClick={() => {
                      clearQueryFilter()
                      setGroupBy(AnswersGroupingType.PeriodName, undefined, true)
                    }}
                    sx={{ ml: 1 }}
                  >
                    {formatMessage({ id: 'filters.clearAll' })}
                  </Button>
                )}
              </Grid>
            </>
          )
        }}
      </Filters>
    </Grid>
  )
}

export default DatahubDataInsightsFilters
